import React, { ChangeEvent, CSSProperties, useEffect, useState } from 'react';
import { Field, change } from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Checkbox, FormControlLabel, Radio } from '@material-ui/core';

import FormFileUpload from 'retired/FormFileUpload';
import * as validators from 'utils/validators';
import { imageTypes } from 'retired/FileUpload/utils';
import FormColorField from 'components/FormColorField';
import type { State } from 'reducers';

import { CAMPAIGN_PAGE_FORM, valueSelector } from './types';

const fieldStyle: CSSProperties = {
  marginBottom: '20px',
};

const notEmpty = validators.notEmpty();
const isHexColorValidator = validators.isHexColor();

const colorFields = [
  {
    label: 'Primary text color *',
    name: 'colorA',
    helperText:
      'This will be used for the text color in the first section and other highlighted areas on the page. Please use a hex code (e.g. #FFFFFF)',
  },
  {
    label: 'Secondary text color *',
    name: 'colorB',
    helperText:
      'This will be used for highlighted text on the second section. Please use a hex code (e.g. #FFFFFF)',
  },
  {
    label: 'Tagline text color *',
    name: 'nameColor',
    helperText: 'Please use a hex code (e.g. #FFFFFF)',
  },
  {
    label: 'Call to action background color *',
    name: 'ctaButtonColor',
    helperText: 'Please use a hex code (e.g. #FFFFFF)',
  },
  {
    label: 'Call to action text color *',
    name: 'ctaTextColor',
    helperText: 'Please use a hex code (e.g. #FFFFFF)',
  },
] as const;

type FieldName =
  | 'colorA'
  | 'colorB'
  | 'nameColor'
  | 'ctaButtonColor'
  | 'ctaTextColor'
  | 'backgroundDeedsColor'
  | 'backgroundHeroColor';

const ColorSquare = (props: { name: FieldName }): JSX.Element | null => {
  const { name } = props;
  const colorSquare: CSSProperties = {
    width: '20px',
    height: '20px',
    borderRadius: '3px',
    border: '1px solid rgba(0, 0, 0, .5)',
    position: 'absolute',
    top: 21,
    right: 0,
    zIndex: 1,
  };
  const color = useSelector((state: State) => valueSelector(state, name));
  return color && isHexColorValidator(color) === undefined ? (
    <div style={{ ...colorSquare, backgroundColor: color }} />
  ) : null;
};

const BackgroundColor = ({ field, label }: { field: FieldName; label: string }): JSX.Element => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const backgroundColor = useSelector((state: State) => valueSelector(state, field));

  const [gradient, setGradient] = useState(!!id);

  const bgCheckboxChange = (_event: ChangeEvent<HTMLInputElement>, newValue: boolean): void => {
    if (newValue) {
      dispatch(change(CAMPAIGN_PAGE_FORM, field, ''));
    }
    setGradient(newValue);
  };

  useEffect(() => {
    if (backgroundColor !== undefined && backgroundColor !== '' && gradient) {
      setGradient(false);
    }
  }, [backgroundColor]);

  const validationForBackgroundColor = (): Array<(value?: string) => string | undefined> => {
    const result = [isHexColorValidator];
    if (!gradient) {
      result.push(notEmpty);
    }
    return result;
  };

  return (
    <>
      <div>
        <FormControlLabel
          control={
            <Checkbox color="primary" checked={Boolean(gradient)} onChange={bgCheckboxChange} />
          }
          label="Gradient color between the Primary and Secondary colors selected above "
        />
      </div>
      {!gradient && (
        <div style={{ position: 'relative' }}>
          <ColorSquare name={field} />
          <Field
            component={FormColorField}
            label={label}
            name={field}
            fullWidth
            helperText="Please use a hex code (e.g. #FFFFFF)"
            validate={validationForBackgroundColor()}
          />
        </div>
      )}
    </>
  );
};

export const StyleSection = ({
  image,
  setImage,
}: {
  image: boolean;
  setImage: (image: boolean) => void;
}): JSX.Element => {
  const backgroundHeroColor = useSelector((state: State) =>
    valueSelector(state, 'backgroundHeroColor')
  );
  const backgroundImage = useSelector((state: State) => valueSelector(state, 'backgroundImage'));

  useEffect(() => {
    if (backgroundHeroColor !== undefined && backgroundHeroColor !== '') {
      setImage(false);
    }
  }, [backgroundHeroColor]);

  useEffect(() => {
    if (backgroundImage) {
      setImage(true);
    }
  }, [backgroundImage]);

  return (
    <>
      {colorFields.map((field) => (
        <div style={{ position: 'relative' }} key={field.name}>
          <ColorSquare name={field.name} />
          <Field
            label={field.label}
            name={field.name}
            helperText={field.helperText}
            style={fieldStyle}
            component={FormColorField}
            fullWidth
            validate={[notEmpty, isHexColorValidator]}
          />
        </div>
      ))}
      <p style={{ marginBottom: '10px', fontWeight: 500 }}>
        Background for the Header and Banner Settings
      </p>
      <div>
        <FormControlLabel
          control={<Radio color="primary" checked={image} onChange={() => setImage(!image)} />}
          label="Upload an image"
        />
      </div>
      <div>
        <FormControlLabel
          control={<Radio color="primary" checked={!image} onChange={() => setImage(!image)} />}
          label="Use colors as the background"
        />
      </div>
      {image && (
        <Field
          id="mainPicture"
          name="backgroundImage"
          value={backgroundImage}
          component={FormFileUpload}
          labelText="Upload an image in PNG or JPG format. This will be optimized for mobile view as well."
          acceptedTypes={imageTypes}
          withCrop
          cropRatio={1504 / 1002}
          imagePreview
        />
      )}
      {!image && (
        <BackgroundColor
          field="backgroundHeroColor"
          label="Background color for the Hero section & Preview Card"
        />
      )}
      <p style={{ marginBottom: '10px', fontWeight: 500 }}>Background for the Deeds Section *</p>
      <BackgroundColor
        field="backgroundDeedsColor"
        label="Background color for the Deeds Section *"
      />
    </>
  );
};
